// Whether a stream is someone speaking: the RMS every 100 ms, with two
// thresholds so it does not flicker at the edge, and a change told only when
// it crosses one (telling every tick would update the store ten times a
// second).
//
// A remote stream is measured only once it plays in an <audio>: Chrome does
// not pass a WebRTC stream that no element plays to the analyser.
import type { CallAudio } from "./audio";

export type LevelWatcher = (
  stream: MediaStream,
  onChange: (speaking: boolean) => void,
) => () => void;

/** Speaking above this RMS (about -34 dBFS). */
export const SPEAKING_ON = 0.02;
/** Stopped speaking below this. */
export const SPEAKING_OFF = 0.01;
export const LEVEL_INTERVAL_MS = 100;

export function nextSpeaking(rms: number, speaking: boolean): boolean {
  return speaking ? rms > SPEAKING_OFF : rms > SPEAKING_ON;
}

export function rmsOf(samples: Float32Array): number {
  let sum = 0;
  for (const v of samples) sum += v * v;
  return samples.length === 0 ? 0 : Math.sqrt(sum / samples.length);
}

export function levelWatcher(audio: CallAudio): LevelWatcher {
  return (stream, onChange) => {
    const ac = audio.ensure();
    if (!ac) return () => {};
    const source = ac.createMediaStreamSource(stream);
    const analyser = ac.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    let speaking = false;
    const timer = setInterval(() => {
      analyser.getFloatTimeDomainData(buf);
      const next = nextSpeaking(rmsOf(buf), speaking);
      if (next !== speaking) {
        speaking = next;
        onChange(next);
      }
    }, LEVEL_INTERVAL_MS);
    return () => {
      clearInterval(timer);
      source.disconnect();
      analyser.disconnect();
    };
  };
}
